import { bunja } from 'bunja';
import { formatISO } from 'date-fns';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { atomWithDefault } from 'jotai/utils';

import { JotaiStoreScope } from '../bunja-jotai';
import { getLocalStorage, setLocalStorage } from '../localStorage';
import segment from '../segment';
import { PrivacyCookieEvent } from '../segment/events';
import { eeaCountries } from './data';
import { getCountryFromVercel } from './utils';

export const CookiePopupHideTimestampKey = 'flipster.configs.v1.cookiePopupTimestamp';
export const CookiePopupTypeKey = 'flipster.configs.v1.cookiePopupType';

export const consentBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  const isConsentRequiredAtom = atom(false);

  const countryQueryAtom = atomWithQuery(() => ({
    queryKey: ['userCountry'],
    queryFn: getCountryFromVercel,
    staleTime: Infinity,
    gcTime: Infinity,
  }));

  const userCountryAtom = atom((get) => {
    const query = get(countryQueryAtom);
    return query.data ?? null;
  });

  const isEeaCountryAtom = atom((get) => {
    const country = get(userCountryAtom);
    return country ? eeaCountries.includes(country) : false;
  });

  // const grantAllTracking = () => {
  //   segment.reset();
  //   segment.load(getSegmentKey('web', getServerEnv()));
  //   datadogLogs.setTrackingConsent('granted');
  // };

  // const denyTracking = () => {
  //   segment.reset();
  //   segment.load(getSegmentKey('web', getServerEnv()), {
  //     disableClientPersistence: true,
  //     disable: true,
  //   });
  //   datadogLogs.setTrackingConsent('not-granted');
  // };

  bunja.effect(() => {
    const hideTimestamp = getLocalStorage(CookiePopupHideTimestampKey);
    if (hideTimestamp) {
      return;
    }

    const unsubscribe = store.sub(countryQueryAtom, () => {
      const query = store.get(countryQueryAtom);

      if (query.isPending) {
        return;
      }

      if (query.isSuccess) {
        const country = query.data;
        if (!country) return;
        if (store.get(consentStatusAtom) !== null) return;
        if (!eeaCountries.includes(country)) {
          // grantAllTracking();
          return;
        }
        store.set(isConsentRequiredAtom, true);
        segment.track(PrivacyCookieEvent.CookiePolicyView, {});
      }
    });

    return unsubscribe;
  });

  const acceptAllCookies = () => {
    segment.track(PrivacyCookieEvent.CookieAcceptAllClick, {});
    // grantAllTracking();

    setLocalStorage(CookiePopupHideTimestampKey, formatISO(new Date()));
    store.set(consentStatusAtom, 'all');
    store.set(isConsentRequiredAtom, false);
  };

  const acceptRequiredCookies = () => {
    segment.track(PrivacyCookieEvent.CookieAcceptReqClick, {});
    // purgeNonEssentialCookies();
    // denyTracking();

    setLocalStorage(CookiePopupHideTimestampKey, formatISO(new Date()));
    store.set(consentStatusAtom, 'required');
    store.set(isConsentRequiredAtom, false);
  };

  // Current consent status atom with atomWithDefault
  const consentStatusBaseAtom = atomWithDefault<'all' | 'required' | null>(() => {
    const consentType = getLocalStorage(CookiePopupTypeKey);
    if (!consentType) return null;
    return consentType as 'all' | 'required';
  });

  const consentStatusAtom = atom(
    (get) => get(consentStatusBaseAtom),
    (_, set, newValue: 'all' | 'required' | null) => {
      if (newValue) {
        setLocalStorage(CookiePopupTypeKey, newValue);
      }
      set(consentStatusBaseAtom, newValue);
    },
  );

  return {
    isConsentRequiredAtom,
    consentStatusAtom,
    userCountryAtom,
    isEeaCountryAtom,
    countryQueryAtom,
    acceptAllCookies,
    acceptRequiredCookies,
  };
});
consentBunja.debugLabel = 'consentBunja';
